import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Sparkles, RefreshCw, Link2, Search, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import Pagination from '../components/Pagination';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InsightCard = ({ insight }) => {
  const getPriorityBadge = (severity) => {
    const styles = {
      high: 'bg-red-500/90 text-white',
      medium: 'bg-orange-500/90 text-white',
      low: 'bg-blue-500/90 text-white',
    };
    return styles[severity] || styles.medium;
  };

  const getTypeInfo = (type) => {
    const types = {
      'super_seeded_price_lower': { label: 'Super Seeded Price Alert', color: 'bg-pink-500/90' },
      'low_gp_opportunity': { label: 'GP Improvement Alert', color: 'bg-amber-500/90' },
      'competitor_pricing_comparison': { label: 'Competitor Pricing', color: 'bg-blue-600/90' },
    };
    return types[type] || { label: 'Pricing Insight', color: 'bg-pink-500/90' };
  };

  const typeInfo = getTypeInfo(insight.type);

  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 space-y-4">
      <div className="flex items-start gap-3">
        {insight.source_url && (
          <a
            href={insight.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex-shrink-0"
            title="View source"
          >
            <Link2 className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
          </a>
        )}

        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-3 py-1.5 rounded ${typeInfo.color} text-white`}>
              {typeInfo.label}
            </span>
            {insight.severity && (
              <span className={`text-xs font-medium px-3 py-1.5 rounded ${getPriorityBadge(insight.severity)}`}>
                {insight.severity} priority
              </span>
            )}
          </div>

          <p className="text-zinc-900 dark:text-white font-medium">
            <span className="font-mono font-semibold">{insight.part_number}</span>
            {insight.part_name && <span className="text-zinc-500 dark:text-zinc-400"> - {insight.part_name}</span>}
            {insight.family_name && <span className="text-zinc-500 dark:text-zinc-400"> - {insight.family_name}</span>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {insight.type === 'competitor_pricing_comparison' ? (
          <>
            <div className="p-4 rounded-lg bg-zinc-100 dark:bg-zinc-800">
              <p className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">Our Price</p>
              <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">
                ₹{(insight.our_price || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-100 dark:bg-zinc-800">
              <p className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">Competitor Price</p>
              <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">
                ₹{(insight.competitor_price || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-teal-600/80">
              <p className="text-xs uppercase tracking-wider text-teal-200 mb-1">Price Gap</p>
              <p className="text-xl font-mono font-bold text-white">
                ₹{Math.abs(insight.price_difference || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-teal-600/80">
              <p className="text-xs uppercase tracking-wider text-teal-200 mb-1">Source</p>
              <p className="text-base font-semibold text-white truncate">{insight.source || '—'}</p>
            </div>
          </>
        ) : insight.type === 'super_seeded_price_lower' ? (
          <>
            <div className="p-4 rounded-lg bg-zinc-100 dark:bg-zinc-800">
              <p className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">Current Price</p>
              <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">
                ₹{(insight.current_price || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-100 dark:bg-zinc-800">
              <p className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">Original Price</p>
              <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">
                ₹{(insight.original_price || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-pink-600/80">
              <p className="text-xs uppercase tracking-wider text-pink-200 mb-1">Price Difference</p>
              <p className="text-xl font-mono font-bold text-white">
                ₹{Math.abs(insight.price_difference || 0).toLocaleString('en-IN')}
                {insight.price_difference_percent != null && (
                  <span className="text-sm ml-1">({insight.price_difference_percent}%)</span>
                )}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-pink-600/80">
              <p className="text-xs uppercase tracking-wider text-pink-200 mb-1">Revenue Recovery</p>
              <p className="text-xl font-mono font-bold text-white">
                ₹{(insight.potential_revenue_recovery || 0).toLocaleString('en-IN')}
              </p>
            </div>
          </>
        ) : (
          <>
            {insight.negative_metrics && Object.entries(insight.negative_metrics).map(([key, value]) => (
              <div key={key} className="p-4 rounded-lg bg-[#C3002F]/80">
                <p className="text-xs uppercase tracking-wider text-red-100 mb-1">
                  {key.replace(/_/g, ' ')}
                </p>
                <p className="text-xl font-mono font-bold text-white">
                  ₹{Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            ))}
          </>
        )}
      </div>

      <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
        {insight.recommendation}
      </p>
    </div>
  );
};

export default function InsightsPage() {
  const { isDark } = useTheme();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastGenerated, setLastGenerated] = useState(null);
  const itemsPerPage = 10;

  const [searchQuery, setSearchQuery] = useState('');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('potential');

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/insights`);
      setInsights(response.data.insights || []);
      setLastGenerated(response.data.generated_at);
    } catch (error) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = async () => {
    setRefreshing(true);

    try {
      const response = await axios.post(`${API}/insights/generate`);
      setInsights(response.data.insights || []);
      setLastGenerated(response.data.generated_at);
      toast.success(response.data.message || 'Insights generated successfully');
    } catch (error) {
      toast.error('Failed to generate insights');
    } finally {
      setRefreshing(false);
    }
  };

  const pricingInsights = insights.filter(insight =>
    insight.type === 'low_gp_opportunity' ||
    insight.type === 'competitor_pricing_comparison' ||
    insight.type === 'super_seeded_price_lower'
  );

  const filteredInsights = pricingInsights.filter(insight => {
    if (searchQuery && insight.part_number) {
      if (!insight.part_number.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
    }
    if (familyFilter !== 'all' && insight.family_name !== familyFilter) {
      return false;
    }
    if (severityFilter !== 'all' && insight.severity !== severityFilter) {
      return false;
    }
    return true;
  });

  const getPriceDelta = (insight) => {
    if (insight.type === 'competitor_pricing_comparison') {
      return Math.abs(insight.price_difference || 0);
    }
    if (insight.type === 'super_seeded_price_lower') {
      return insight.potential_revenue_recovery || 0;
    }
    if (insight.type === 'low_gp_opportunity' && insight.negative_metrics) {
      return Math.max(...Object.values(insight.negative_metrics).map(Math.abs));
    }
    return insight.price_increase_needed || 0;
  };

  const sortedInsights = [...filteredInsights].sort((a, b) => {
    if (sortBy === 'potential') {
      return getPriceDelta(b) - getPriceDelta(a);
    } else if (sortBy === 'severity') {
      const severityOrder = { high: 0, medium: 1, low: 2, info: 3 };
      return severityOrder[a.severity || 'info'] - severityOrder[b.severity || 'info'];
    }
    return 0;
  });

  const uniqueFamilies = [...new Set(pricingInsights.map(i => i.family_name).filter(Boolean))];

  const totalPages = Math.ceil(sortedInsights.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInsights = sortedInsights.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, familyFilter, severityFilter, sortBy]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-zinc-500 dark:text-zinc-400">Analyzing pricing data...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8 space-y-6" data-testid="insights-page">
        {/* Hero Banner */}
        <div className="relative p-6 rounded-xl overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
          <div />

          <div className="relative z-10 flex items-center gap-6">
            <div className="p-4 rounded-xl border bg-[#C3002F]/20 border-[#C3002F]/40">
              <Sparkles className="w-8 h-8 text-[#C3002F]" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Revenue Optimization Engine</h1>
              <p className={`mt-1 max-w-2xl text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                Our AI analyzes pricing patterns across super seeded parts, orientation pairs (left/right),
                similar vehicle parts, and family averages to identify revenue growth opportunities.
              </p>
            </div>
          </div>

          <div className="absolute top-6 right-6 z-20">
            <Button
              onClick={generateInsights}
              disabled={refreshing}
              variant="outline"
              className="border-zinc-600 bg-zinc-800/50 hover:bg-zinc-700/50 text-white"
              data-testid="refresh-insights-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Generating...' : 'Refresh Insights'}
            </Button>
          </div>
        </div>

        {/* Recommendations Count */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
            {sortedInsights.length} {sortedInsights.length === 1 ? 'Recommendation' : 'Recommendations'} Found
          </h2>
          {lastGenerated && (
            <span className="text-xs text-zinc-500">
              Last updated: {new Date(lastGenerated).toLocaleString()}
            </span>
          )}
        </div>

        {/* Filters */}
        {pricingInsights.length > 0 && (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by part number..."
                className="pl-10 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white"
              />
            </div>

            <Select value={familyFilter} onValueChange={setFamilyFilter}>
              <SelectTrigger className="w-44 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">All Families</SelectItem>
                {uniqueFamilies.map(family => (
                  <SelectItem key={family} value={family}>{family}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-40 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="high">High Priority</SelectItem>
                <SelectItem value="medium">Medium Priority</SelectItem>
                <SelectItem value="low">Low Priority</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-52 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="potential">Highest Potential First</SelectItem>
                <SelectItem value="severity">Severity First</SelectItem>
              </SelectContent>
            </Select>

            {(searchQuery || familyFilter !== 'all' || severityFilter !== 'all' || sortBy !== 'potential') && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setFamilyFilter('all');
                  setSeverityFilter('all');
                  setSortBy('potential');
                }}
                className="border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {/* Insights Grid */}
        {sortedInsights.length > 0 ? (
          <div className="space-y-4">
            {paginatedInsights.map((insight, index) => (
              <InsightCard key={index} insight={insight} />
            ))}

            {sortedInsights.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={sortedInsights.length}
                itemsPerPage={itemsPerPage}
                className="mt-6"
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
            <Sparkles className="w-16 h-16 text-zinc-300 dark:text-zinc-700 mb-4" />
            <h3 className="text-xl font-semibold text-zinc-600 dark:text-zinc-400">No Pricing Recommendations</h3>
            <p className="text-zinc-500 mt-2 max-w-md">
              Click "Refresh Insights" to generate AI-powered pricing recommendations based on your data.
            </p>
            <Button
              onClick={generateInsights}
              disabled={refreshing}
              className="mt-6 bg-purple-600 hover:bg-purple-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Generate Insights
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
